import { useEffect, useMemo, useCallback, useRef } from "react"
import { Flex, Input, Form } from "antd"
import { QuestionCircleOutlined } from "@ant-design/icons"
import { useParams } from "react-router-dom"
import { getActiveSetValue, getConditionalRevertValue, shouldRevertConditionalValue, shouldSkipProfileAutofill } from "../../../utils/formsHelper"
import { useSelector } from "react-redux"
import { getPreviousDataByValue, getPublicPreviousDataByValue } from "../../../services/requestServices"

const stripNonAlphanumeric = (event) => {
    const raw = event?.target?.value ?? event ?? ""
    return String(raw).replace(/[^a-zA-Z0-9]/g, "")
}

const Text = (props) => {
    const { spaceId, requestId } = useParams()
    const { label, placeholder, tooltip, helpText, mandatory, readOnly, id, previewMode, previewmode, setValueData, form, minNumber, maxNumber, alphanumeric, duplicateCheck, mapProfile, profileField, requestDetail, enablePreviousDataFetch, isPublicForm = false } = props
    const isPreview = previewMode || previewmode;

    const { userInfo } = useSelector((state) => state.user)

    const isEditing = !!(requestDetail && Object.keys(requestDetail).length > 0);
    const skipProfileAutofill = shouldSkipProfileAutofill({
        requestDetail,
        defaultRequestDetail: props.defaultRequestDetail,
        userEmail: userInfo?.email
    })
    const routeRequestId = requestId || props.requestId
    const requestContextReady = !routeRequestId || routeRequestId === "new"
        || !!(requestDetail?.requestId || props.defaultRequestDetail?.requestId)

    const profileValue = useMemo(() => {
        if (!mapProfile || !profileField || !userInfo?.userId || skipProfileAutofill || !requestContextReady) return ""
        if (profileField === "name") return userInfo.name || ""
        if (profileField === "email") return userInfo.email || ""
        if (profileField === "employeeId") return userInfo.employeeId || userInfo.userId || ""
        return ""
    }, [mapProfile, profileField, userInfo, skipProfileAutofill, requestContextReady])

    // Maintain previous value (used to allow current value during edit)
    const prevValueRef = useRef("");

    const rules = useMemo(() => {
        const next = []
        if (mandatory) {
            next.push({ required: true, whitespace: true, message: `${label} is required` })
        }
        if (minNumber && parseInt(minNumber) > 0) {
            next.push({
                min: parseInt(minNumber),
                message: `${label} must be at least ${minNumber} characters`
            })
        }
        if (maxNumber && parseInt(maxNumber) > 0) {
            next.push({
                max: parseInt(maxNumber),
                message: `${label} must not exceed ${maxNumber} characters`
            })
        }
        if (alphanumeric) {
            next.push({
                pattern: /^[a-zA-Z0-9]*$/,
                message: `${label} must contain only letters and numbers`
            })
        }
        if (duplicateCheck) {
            next.push({
                validator: async (_, value) => {
                    if (!value) return Promise.resolve()
                    try {
                        const storageKey = `ff:dupe:${id}`
                        const raw = localStorage.getItem(storageKey)
                        const list = raw ? JSON.parse(raw) : []
                        const exists = Array.isArray(list) && list.includes(value)
                        if (exists && value !== prevValueRef.current) {
                            return Promise.reject(new Error(`${label} must be unique`))
                        }
                        return Promise.resolve()
                    } catch {
                        return Promise.resolve()
                    }
                }
            })
        }
        return next
    }, [mandatory, label, minNumber, maxNumber, alphanumeric, duplicateCheck, id])

    const disabled = previewMode && readOnly ? true : false
    const help = useMemo(
        () => (tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> } : undefined),
        [tooltip, helpText]
    )
    const maxLength = maxNumber ? parseInt(maxNumber) : undefined
    const profileInitialValue = !skipProfileAutofill && isPreview && !(requestId && requestId !== "new")
        ? profileValue
        : undefined

    const activeSetValue = getActiveSetValue(setValueData, true)

    // Track the condition value already applied so an editable field is prefilled
    // only once. Disabled fields stay locked to the condition value.
    const lastAppliedConditionalRef = useRef(undefined)

    useEffect(() => {
        try {
            const initial = form?.getFieldValue(id);
            if (initial !== undefined) prevValueRef.current = initial;
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBlur = useCallback((e) => {
        const value = e.target.value;
        if (enablePreviousDataFetch && isPreview && !isEditing && value) {
            const fetchPreviousData = async () => {
                const response = isPublicForm
                    ? await getPublicPreviousDataByValue(id, value, props.requestId, props.spaceId, props.shareToken)
                    : await getPreviousDataByValue(id, value, spaceId, requestId);
                const fetchedValues = response?.data?.value;
                if (response.status === 200 && fetchedValues && typeof fetchedValues === "object") {
                    const fieldsToSet = { ...fetchedValues };
                    delete fieldsToSet[id];
                    if (Object.keys(fieldsToSet).length > 0) {
                        form?.setFieldsValue(fieldsToSet);
                    }
                }
            }
            fetchPreviousData()
        }
        if (!duplicateCheck) return;
        try {
            if (!value) return;
            const storageKey = `ff:dupe:${id}`;
            const raw = localStorage.getItem(storageKey);
            const list = raw ? JSON.parse(raw) : [];
            if (!Array.isArray(list)) return;
            if (!list.includes(value)) {
                list.push(value);
                localStorage.setItem(storageKey, JSON.stringify(list));
            }
            prevValueRef.current = value;
        } catch {
            // ignore
        }
    }, [duplicateCheck, enablePreviousDataFetch, isPreview, isEditing, isPublicForm, id, form, spaceId, requestId, props.requestId, props.spaceId, props.shareToken]);

    useEffect(() => {
        if (activeSetValue?.value) {
            const key = activeSetValue.value
            if (disabled) {
                const current = form?.getFieldValue(id)
                if (current !== key) {
                    form?.setFieldValue(id, key)
                }
            } else if (lastAppliedConditionalRef.current !== key) {
                form?.setFieldValue(id, key)
            }
            lastAppliedConditionalRef.current = key
        } else if (shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: form?.getFieldValue(id), appliedValue: lastAppliedConditionalRef.current })) {
            form?.setFieldValue(id, getConditionalRevertValue(id, props.defaultFormValues))
            lastAppliedConditionalRef.current = undefined
        } else {
            lastAppliedConditionalRef.current = undefined
        }
    }, [activeSetValue, form, id, disabled])

    useEffect(() => {
        if (mapProfile && profileField && isPreview && userInfo?.userId && !skipProfileAutofill && requestContextReady) {
            // Delay slightly to ensure form is fully initialized and any resetFields has run
            const timer = setTimeout(() => {
                const currentValue = form?.getFieldValue(id);
                if (!currentValue && profileValue) {
                    form?.setFieldValue(id, profileValue);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [mapProfile, profileField, isPreview, userInfo, form, id, skipProfileAutofill, requestContextReady, profileValue]);

    return (
        <div className="form-element">
            <Flex vertical>
                <Form.Item
                    name={id}
                    label={label}
                    tooltip={help}
                    rules={rules}
                    getValueFromEvent={alphanumeric ? stripNonAlphanumeric : undefined}
                    initialValue={profileInitialValue}
                >
                    <Input
                        placeholder={placeholder}
                        disabled={disabled}
                        size="large"
                        maxLength={maxLength}
                        onBlur={handleBlur}
                        showCount={!!maxNumber}
                    />
                </Form.Item>
            </Flex>
        </div>
    )
}

export default Text
